/**
 * Rubric Slice — Redux state management for rubric operations.
 * Handles rubric list, generation, update, and approval states.
 *
 * @module Store/rubricSlice
 */

import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  rubrics: [],
  // Atom rubrics that actually score Maths/Physics/Chemistry/Biology questions
  // (PRD-MANAGE-RUBRICS-ATOMS) — one entry per atomic-subject descriptive Q.
  atomicRubrics: [],
  atomicSubjects: [],
  atomLibrary: [],
  savingAtoms: false,
  loading: false,
  error: null,
  generating: false,
  approving: false,
  saving: false,
  selectedAssessmentId: null,
  descriptiveCount: 0,
  totalQuestions: 0,
  // Re-evaluation state
  reEvaluating: false,
  reEvaluationError: null
};

const rubricSlice = createSlice({
  name: 'rubric',
  initialState,
  reducers: {
    // Fetch rubrics
    setRubricsLoading: (state) => {
      state.loading = true;
      state.error = null;
    },
    setRubricsData: (state, action) => {
      state.rubrics = action.payload.rubrics;
      state.atomicRubrics = action.payload.atomicRubrics ?? [];
      state.atomicSubjects = action.payload.atomicSubjects ?? state.atomicSubjects;
      state.selectedAssessmentId = action.payload.assessmentId || state.selectedAssessmentId;
      state.descriptiveCount = action.payload.descriptiveCount ?? state.descriptiveCount;
      state.totalQuestions = action.payload.totalQuestions ?? state.totalQuestions;
      state.loading = false;
      state.error = null;
    },
    setRubricsError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Generate rubrics
    setGenerating: (state) => {
      state.generating = true;
      state.error = null;
    },
    setGeneratingDone: (state, action) => {
      state.generating = false;
      state.rubrics = action.payload || state.rubrics;
    },
    setGeneratingError: (state, action) => {
      state.generating = false;
      state.error = action.payload;
    },

    // Update single rubric
    setSaving: (state) => {
      state.saving = true;
      state.error = null;
    },
    setSavingDone: (state, action) => {
      state.saving = false;
      const updated = action.payload;
      const idx = state.rubrics.findIndex(r => r._id === updated._id);
      if (idx !== -1) {
        state.rubrics[idx] = updated;
      }
    },
    setSavingError: (state, action) => {
      state.saving = false;
      state.error = action.payload;
    },

    // Approve all
    setApproving: (state) => {
      state.approving = true;
      state.error = null;
    },
    setApprovingDone: (state) => {
      state.approving = false;
      state.rubrics = state.rubrics.map(r => ({ ...r, isApproved: true }));
    },
    setApprovingError: (state, action) => {
      state.approving = false;
      state.error = action.payload;
    },

    // Atom override save (per-assessment, per-shape)
    setSavingAtoms: (state) => {
      state.savingAtoms = true;
      state.error = null;
    },
    setSavingAtomsDone: (state) => {
      state.savingAtoms = false;
    },
    setSavingAtomsError: (state, action) => {
      state.savingAtoms = false;
      state.error = action.payload;
    },
    setAtomLibrary: (state, action) => {
      state.atomLibrary = action.payload || [];
    },

    // Re-evaluation
    setReEvaluating: (state) => {
      state.reEvaluating = true;
      state.reEvaluationError = null;
    },
    setReEvaluatingDone: (state) => {
      state.reEvaluating = false;
    },
    setReEvaluatingError: (state, action) => {
      state.reEvaluating = false;
      state.reEvaluationError = action.payload;
    },

    // Reset
    resetRubricState: () => initialState
  }
});

export const {
  setRubricsLoading,
  setRubricsData,
  setRubricsError,
  setGenerating,
  setGeneratingDone,
  setGeneratingError,
  setSaving,
  setSavingDone,
  setSavingError,
  setApproving,
  setApprovingDone,
  setApprovingError,
  setSavingAtoms,
  setSavingAtomsDone,
  setSavingAtomsError,
  setAtomLibrary,
  setReEvaluating,
  setReEvaluatingDone,
  setReEvaluatingError,
  resetRubricState
} = rubricSlice.actions;

// Selectors
export const selectRubrics = (state) => state.rubric.rubrics;
export const selectRubricLoading = (state) => state.rubric.loading;
export const selectRubricError = (state) => state.rubric.error;
export const selectRubricGenerating = (state) => state.rubric.generating;
export const selectRubricApproving = (state) => state.rubric.approving;
export const selectRubricSaving = (state) => state.rubric.saving;
export const selectSelectedAssessmentId = (state) => state.rubric.selectedAssessmentId;
export const selectDescriptiveCount = (state) => state.rubric.descriptiveCount;
export const selectTotalQuestions = (state) => state.rubric.totalQuestions;
export const selectReEvaluating = (state) => state.rubric.reEvaluating;
export const selectReEvaluationError = (state) => state.rubric.reEvaluationError;
export const selectAtomicRubrics = (state) => state.rubric.atomicRubrics;
export const selectAtomicSubjects = (state) => state.rubric.atomicSubjects;
export const selectAtomLibrary = (state) => state.rubric.atomLibrary;
export const selectSavingAtoms = (state) => state.rubric.savingAtoms;

export default rubricSlice.reducer;
